// src/admin/context/AdminAuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { authAPI } from "../services/api";

const AdminAuthContext = createContext(null);

const DEFAULT_ADMIN = {
  name: "Selva Kumar",
  role: "Director & Lead Faculty",
  email: "admin@gmail.com",
  phone: "+91 98400 12345",
  avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&q=80",
  academy: "Selva's IAS Academy",
  branch: "Chennai Main Campus",
};

export const AdminAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("selvas_admin_authenticated") === "true";
  });

  const [adminUser, setAdminUser] = useState(() => {
    const saved = localStorage.getItem("selvas_admin_user");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return DEFAULT_ADMIN;
      }
    }
    return DEFAULT_ADMIN;
  });

  const [authError, setAuthError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Verify backend session on mount if marked authenticated
  useEffect(() => {
    const verifySession = async () => {
      const token = localStorage.getItem("selvas_admin_token");
      if (token) {
        try {
          const res = await authAPI.getMe();
          if (res.success && res.admin) {
            setAdminUser(res.admin);
            setIsAuthenticated(true);
            localStorage.setItem("selvas_admin_user", JSON.stringify(res.admin));
          }
        } catch (e) {
          // If token expired, clear
          console.warn("Backend session check failed:", e.message);
        }
      }
    };
    verifySession();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem("selvas_admin_authenticated", "true");
      localStorage.setItem("selvas_admin_user", JSON.stringify(adminUser));
    } else {
      localStorage.removeItem("selvas_admin_authenticated");
    }
  }, [isAuthenticated, adminUser]);

  const login = async (email, password, rememberMe = false) => {
    setAuthError("");
    setIsLoading(true);
    const trimmedEmail = (email || "").trim().toLowerCase();
    const trimmedPassword = (password || "").trim();

    try {
      // 1. Authenticate with backend API (MongoDB Admin collection)
      const res = await authAPI.login(trimmedEmail, trimmedPassword, rememberMe);
      if (res.success) {
        if (res.token) {
          localStorage.setItem("selvas_admin_token", res.token);
        }
        const user = res.admin || DEFAULT_ADMIN;
        setAdminUser(user);
        setIsAuthenticated(true);
        if (rememberMe) {
          localStorage.setItem("selvas_admin_remember", "true");
        }
        setIsLoading(false);
        return { success: true, admin: user };
      }
      setIsLoading(false);
      const err = res.message || "Invalid email or password.";
      setAuthError(err);
      return { success: false, error: err };
    } catch (apiError) {
      const errorMsg =
        apiError?.message || "Invalid email or password. Please verify your credentials.";
      setAuthError(errorMsg);
      setIsLoading(false);
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (e) {
      // Ignore logout errors
    }
    setIsAuthenticated(false);
    localStorage.removeItem("selvas_admin_authenticated");
    localStorage.removeItem("selvas_admin_token");
    setAuthError("");
  };

  const updateProfile = async (updates) => {
    try {
      const res = await authAPI.updateProfile(updates);
      if (res.success && res.admin) {
        setAdminUser(res.admin);
        localStorage.setItem("selvas_admin_user", JSON.stringify(res.admin));
        return { success: true };
      }
    } catch (e) {
      // Fallback to local
    }
    setAdminUser((prev) => {
      const updated = { ...prev, ...updates };
      localStorage.setItem("selvas_admin_user", JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const res = await authAPI.changePassword(currentPassword, newPassword);
      return res;
    } catch (err) {
      return { success: false, message: err.message };
    }
  };

  return (
    <AdminAuthContext.Provider
      value={{
        isAuthenticated,
        adminUser,
        authError,
        isLoading,
        setAuthError,
        login,
        logout,
        updateProfile,
        changePassword,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within an AdminAuthProvider");
  }
  return context;
};
